import { useEffect, useState, useMemo } from 'react';
import { API_BASE } from "./config";
import socket from "../socket/socket";
import '../css/PlayerManager.css';
import { openAdminPlayerEditor, getPlayIntensity } from '../utils/playIntensity';
import { matchesSearch } from '../utils/textSearch';

function PlayerManager() {

  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');

  const [players, setPlayers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [updatingStatusUserId, setUpdatingStatusUserId] = useState(null);
  const [search, setSearch] = useState('');
  const [sortMode, setSortMode] = useState('POINT_DESC');

  /* ================= LOAD GROUPS ================= */

  const fetchGroups = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/groups`);
      const data = await res.json();
      const groupList = Array.isArray(data) ? data : [];
      setGroups(groupList);

      // auto select group แรก
      if (groupList.length > 0) {
        setSelectedGroup(currentGroup => currentGroup || groupList[0].id);
      }

    } catch (err) {
      console.error("Load groups error:", err);
    }
  };

  /* ================= LOAD PLAYERS ================= */

  const fetchPlayers = async (groupId) => {
    if (!groupId) return;

    try {
      const res = await fetch(`${API_BASE}/api/players/${groupId}`);
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || data.error || 'โหลดรายชื่อผู้เล่นไม่สำเร็จ');
      }

      if (Array.isArray(data)) {
        setPlayers(data);
      } else if (Array.isArray(data?.data)) {
        setPlayers(data.data);
      } else {
        setPlayers([]);
      }

    } catch (err) {
      console.error("Fetch players error:", err);
      setPlayers([]);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      fetchPlayers(selectedGroup);
    }
  }, [selectedGroup]);

  /* ================= REALTIME PLAYERS ================= */
  useEffect(() => {
    if (!selectedGroup) return undefined;

    const groupId = Number(selectedGroup);

    const refreshPlayers = (data = {}) => {
      const eventGroupId = data.groupId ?? data.group_id;

      if (
        eventGroupId !== undefined &&
        eventGroupId !== null &&
        Number(eventGroupId) !== groupId
      ) {
        return;
      }

      console.log('👤 PLAYER LIST UPDATED:', data);
      fetchPlayers(groupId);
    };

    const handleConnect = () => {
      console.log('📡 PlayerManager join-group:', groupId);
      socket.emit('join-group', groupId);

      // หาก event เกิดตอน Socket หลุด ให้ดึงข้อมูลล่าสุดเมื่อเชื่อมต่อกลับมา
      fetchPlayers(groupId);
    };

    socket.on('connect', handleConnect);
    socket.on('player-added', refreshPlayers);
    socket.on('member-joined', refreshPlayers);
    socket.on('member-updated', refreshPlayers);
    socket.on('member-checkout', refreshPlayers);
    socket.on('player-status-updated', refreshPlayers);
    socket.on('match-started', refreshPlayers);
    socket.on('match-ended', refreshPlayers);

    if (socket.connected) {
      handleConnect();
    }

    return () => {
      socket.off('connect', handleConnect);
      socket.off('player-added', refreshPlayers);
      socket.off('member-joined', refreshPlayers);
      socket.off('member-updated', refreshPlayers);
      socket.off('member-checkout', refreshPlayers);
      socket.off('player-status-updated', refreshPlayers);
      socket.off('match-started', refreshPlayers);
      socket.off('match-ended', refreshPlayers);
    };
  }, [selectedGroup]);

  /* ================= ACTIONS ================= */

  const removePlayer = async (userId) => {
    if (!window.confirm('ลบผู้เล่นคนนี้?')) return;

    await fetch(`${API_BASE}/api/players/${selectedGroup}/${userId}`, {
      method: 'DELETE'
    });

    fetchPlayers(selectedGroup);
  };

  const updateStatus = async (userId, nextStatus) => {
    if (nextStatus === 'CHECKOUT') {
      await checkoutPlayer(userId);
      return;
    }

    if (!selectedGroup || updatingStatusUserId !== null) return;

    try {
      setUpdatingStatusUserId(userId);

      const res = await fetch(
        `${API_BASE}/api/players/${selectedGroup}/${userId}/status`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            status: nextStatus
          })
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data.message ||
          data.sqlMessage ||
          data.error ||
          `อัปเดตสถานะไม่สำเร็จ (HTTP ${res.status})`
        );
      }

      // อัปเดตหน้าจอทันที ไม่ต้องรอ Socket
      setPlayers(currentPlayers =>
        currentPlayers.map(player =>
          Number(player.user_id) === Number(userId)
            ? { ...player, status: nextStatus }
            : player
        )
      );

      // ดึงข้อมูลจริงจากฐานข้อมูลเพื่อยืนยันสถานะอีกครั้ง
      await fetchPlayers(selectedGroup);
    } catch (error) {
      alert(`❌ ${error.message}`);
    } finally {
      setUpdatingStatusUserId(null);
    }
  };

  const checkoutPlayer = async userId => {
    if (!selectedGroup || updatingStatusUserId !== null) return;
    if (!window.confirm('ยืนยัน Check-out ผู้ใช้นี้?')) return;

    try {
      setUpdatingStatusUserId(userId);

      const res = await fetch(`${API_BASE}/api/checkin/admin/${userId}`, {
        method: 'DELETE'
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data.message ||
          data.sqlMessage ||
          data.error ||
          `Check-out ไม่สำเร็จ (HTTP ${res.status})`
        );
      }

      // เอาออกจากรายการทันทีเหมือนหน้า Dashboard
      setPlayers(currentPlayers =>
        currentPlayers.filter(
          player => Number(player.user_id) !== Number(userId)
        )
      );

      // โหลดจากฐานข้อมูลเพื่อยืนยันข้อมูลล่าสุด
      await fetchPlayers(selectedGroup);
    } catch (error) {
      alert(`❌ ${error.message}`);
    } finally {
      setUpdatingStatusUserId(null);
    }
  };

  const saveEdit = async () => {
    await fetch(`${API_BASE}/api/players/${editing.user_id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editing)
    });

    setEditing(null);
    fetchPlayers(selectedGroup);
  };

  /* ================= FILTER + SORT ================= */

  const filteredPlayers = useMemo(() => {

    let list = [...players];

    if (search.trim() !== '') {
      list = list.filter(p =>
        matchesSearch(p.name, search)
      );
    }

    if (sortMode === 'POINT_DESC') {
      list.sort((a, b) => (b.point || 0) - (a.point || 0));
    }

    if (sortMode === 'POINT_ASC') {
      list.sort((a, b) => (a.point || 0) - (b.point || 0));
    }

    if (sortMode === 'NAME') {
      list.sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
      );
    }

    return list;

  }, [players, search, sortMode]);

  /* ================= RENDER ================= */

  return (
    <div className="pm-container">

      <h2 className="pm-title">👥 จัดการผู้เล่น</h2>

      {/* ================= GROUP SELECT ================= */}
      <div className="pm-group-select">
        <label>เลือก Group</label>
        <select
          value={selectedGroup}
          onChange={e => setSelectedGroup(e.target.value)}
        >
          {groups.map(g => (
            <option key={g.id} value={g.id}>
              {g.Group_Name}
            </option>
          ))}
        </select>
      </div>

      {/* ================= TOOLBAR ================= */}
      <div className="pm-toolbar">

        <input
          className="pm-search"
          placeholder="🔍 ค้นหาชื่อผู้เล่น..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        <select
          value={sortMode}
          onChange={e => setSortMode(e.target.value)}
        >
          <option value="POINT_DESC">คะแนนมาก → น้อย</option>
          <option value="POINT_ASC">คะแนนน้อย → มาก</option>
          <option value="NAME">เรียงตามชื่อ</option>
        </select>

      </div>

      {/* ================= EMPTY ================= */}
      {players.length === 0 && (
        <div className="pm-empty">
          ไม่พบผู้เล่น
        </div>
      )}

      {/* ================= LIST ================= */}
      <div className="pm-list">

        {filteredPlayers.map(p => {

          const isEditing = editing?.user_id === p.user_id;

          return (
            <div key={p.user_id} className="pm-card">

              <div className="pm-row">

                <div className="pm-field">
                  <label>ชื่อ</label>
                  {isEditing ? (
                    <input
                      value={editing.name}
                      onChange={e =>
                        setEditing({ ...editing, name: e.target.value })
                      }
                    />
                  ) : (
                    <button type="button" className="admin-inline-player-name" onClick={()=>openAdminPlayerEditor(p)}>{p.name}</button>
                  )}
                </div>

                <div className="pm-field">
                  <label>คะแนน</label>
                  {isEditing ? (
                    <input
                      type="number"
                      value={editing.point}
                      onChange={e =>
                        setEditing({
                          ...editing,
                          point: +e.target.value
                        })
                      }
                    />
                  ) : (
                    <span className="pm-badge">{p.point}</span>
                  )}
                </div>

                <div className="pm-field">
                  <label>Rank</label>
                  {isEditing ? (
                    <input
                      value={editing.level_rank}
                      onChange={e =>
                        setEditing({
                          ...editing,
                          level_rank: e.target.value
                        })
                      }
                    />
                  ) : (
                    <span>{p.level_rank} · {getPlayIntensity(p.play_intensity).icon} {getPlayIntensity(p.play_intensity).label}</span>
                  )}
                </div>

                <div className="pm-field">
                  <label>Status</label>
                  <span className={`pm-status ${p.status || 'EMPTY'}`}>
                    {p.status || '-'}
                  </span>
                </div>

              </div>

              <div className="pm-actions">

                {isEditing ? (
                  <>
                    <button className="pm-save" onClick={saveEdit}>
                      💾 Save
                    </button>
                    <button
                      className="pm-cancel"
                      onClick={() => setEditing(null)}
                    >
                      ❌ Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="pm-edit"
                      onClick={() => setEditing(p)}
                    >
                      ✏️ Edit
                    </button>

                    <button
                      className="pm-delete"
                      onClick={() => removePlayer(p.user_id)}
                    >
                      🗑 Delete
                    </button>

                    <select
                      className="pm-status-select"
                      value={p.status === 'PLAY' ? 'PLAY' : 'ACTIVE'}
                      disabled={
                        Number(updatingStatusUserId) === Number(p.user_id)
                      }
                      onChange={event =>
                        updateStatus(p.user_id, event.target.value)
                      }
                    >
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="PLAY">PLAY</option>
                      <option value="CHECKOUT">CHECKOUT</option>
                    </select>
                  </>
                )}

              </div>

            </div>
          );
        })}

      </div>

    </div>
  );
}

export default PlayerManager;
